import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputNumber, InputNumberChangeEvent } from "primereact/inputnumber";
import { Panel } from "primereact/panel";
import { ChangeEvent, ReactNode, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "react-query";
import { queryKeys } from "../../api/query";
import { useDeleteChecklist } from "../../api/queries/checklists/useDeleteChecklist";
import { useUpdateChecklist } from "../../api/queries/checklists/useUpdateChecklist";
import { useChecklist } from "../../api/queries/checklists/useChecklist";
import {
  CreateChecklistPayload,
  UpdateChecklistPayload,
} from "../../api/types/checklist";
import { useCreateChecklist } from "../../api/queries/checklists/useCreateChecklist";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { useJourneyTypes } from "../../api/queries/enum/useJourneyTypes";
import { Checkbox, CheckboxChangeEvent } from "primereact/checkbox";
import { orderIndexSort } from "../../common/utils";

export default function ChecklistForm() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const createChecklist = useCreateChecklist();
  const deleteChecklist = useDeleteChecklist();
  const updateChecklist = useUpdateChecklist(uuid);
  const { data: checklist } = useChecklist(uuid);
  const { data: journeyTypesRes } = useJourneyTypes();

  const [name, setName] = useState<string>("");
  const onNameChange = (event: ChangeEvent<HTMLInputElement>): void =>
    setName(event.target.value);

  const [journeyType, setJourneyType] = useState<string>("");
  const onJourneyTypeChange = (event: DropdownChangeEvent) =>
    setJourneyType(event.value);

  const [isDefault, setIsDefault] = useState<boolean>(false);
  const onIsDefaultChange = (event: CheckboxChangeEvent) =>
    setIsDefault(event.checked || false);

  const [stages, setStages] = useState<FormStageValue[]>([]);

  // stage
  const onNewStageClick = () =>
    setStages([
      ...stages,
      {
        id: null,
        name: "",
        order_index: 1,
        tasks: [
          {
            id: null,
            name: "",
            description: "",
            order_index: 1,
          },
        ],
      },
    ]);

  const onStageNameChange = (
    event: ChangeEvent<HTMLInputElement>,
    stageIndex: number,
  ) => {
    setStages(
      stages.map((stage, i) => {
        if (i === stageIndex) {
          stage.name = event.target.value;
        }

        return stage;
      }),
    );
  };

  const onStageOrderIndexChange = (
    event: InputNumberChangeEvent,
    stageIndex: number,
  ) => {
    setStages(
      stages.map((stage, i) => {
        if (i === stageIndex && event.value !== null) {
          stage.order_index = event.value;
        }

        return stage;
      }),
    );
  };

  const onRemoveStageClick = (stageIndex: number) => {
    setStages(stages.filter((_, i) => i !== stageIndex));
  };

  // task
  const onNewTaskClick = (stageIndex: number) => {
    setStages(
      stages.map((stage, i) => {
        if (i === stageIndex) {
          stage.tasks.push({
            id: null,
            name: "",
            description: "",
            order_index: 1,
          });
        }

        return stage;
      }),
    );
  };

  const onStageTaskNameChange = (
    event: ChangeEvent<HTMLInputElement>,
    stageIndex: number,
    taskIndex: number,
  ) => {
    setStages(
      stages.map((stage, i) => {
        if (i === stageIndex) {
          stage.tasks.map((task, j) => {
            if (j === taskIndex) {
              task.name = event.target.value;
            }

            return task;
          });
        }

        return stage;
      }),
    );
  };

  const onStageTaskDescriptionChange = (
    event: ChangeEvent<HTMLInputElement>,
    stageIndex: number,
    taskIndex: number,
  ) => {
    setStages(
      stages.map((stage, i) => {
        if (i === stageIndex) {
          stage.tasks.map((task, j) => {
            if (j === taskIndex) {
              task.description = event.target.value;
            }

            return task;
          });
        }

        return stage;
      }),
    );
  };

  const onStageTaskOrderIndexChange = (
    event: InputNumberChangeEvent,
    stageIndex: number,
    taskIndex: number,
  ) => {
    setStages(
      stages.map((stage, i) => {
        if (i === stageIndex) {
          stage.tasks.map((task, j) => {
            if (j === taskIndex && event.value !== null) {
              task.order_index = event.value;
            }

            return task;
          });
        }

        return stage;
      }),
    );
  };

  const onRemoveStageTaskClick = (stageIndex: number, taskIndex: number) => {
    setStages(
      stages.map((stage, i) => {
        if (i === stageIndex) {
          stage.tasks = stage.tasks.filter((_, j) => j !== taskIndex);
        }

        return stage;
      }),
    );
  };

  const handleSubmit = async (event: any) => {
    event.preventDefault();

    const isCloning = event.nativeEvent.submitter.value === "save-new";

    if (uuid && !isCloning) {
      const payload: UpdateChecklistPayload = {
        name,
        context: journeyType,
        default: isDefault,
        stages: stages.map((stage) => ({
          id: stage.id,
          name: stage.name,
          order_index: stage.order_index,
          tasks: stage.tasks.map((task) => ({
            id: task.id,
            name: task.name,
            description: task.description,
            order_index: task.order_index,
          })),
        })),
      };

      await updateChecklist.mutateAsync(payload);
      queryClient.invalidateQueries(queryKeys.checklist(uuid));
    } else {
      const payload: CreateChecklistPayload = {
        name,
        context: journeyType,
        default: isDefault,
        stages: stages.map((stage) => ({
          name: stage.name,
          order_index: stage.order_index,
          tasks: stage.tasks.map((task) => ({
            name: task.name,
            description: task.description,
            order_index: task.order_index,
          })),
        })),
      };

      await createChecklist.mutateAsync(payload);
    }

    // TODO: validations

    queryClient.invalidateQueries(queryKeys.checklistList(), {
      refetchInactive: true,
    });
    navigate("/checklists");
  };

  const onDelete = async () => {
    await deleteChecklist.mutateAsync(uuid);

    queryClient.invalidateQueries(queryKeys.checklist(uuid));
    queryClient.invalidateQueries(queryKeys.checklistList(), {
      refetchInactive: true,
    });

    navigate("/checklists");
  };

  useEffect(() => {
    if (!uuid) {
      setStages([
        {
          id: null,
          name: "",
          order_index: 1,
          tasks: [
            {
              id: null,
              name: "",
              description: "",
              order_index: 1,
            },
          ],
        },
      ]);
    }
  }, [uuid]);

  useEffect(() => {
    if (checklist) {
      setName(checklist.data.name);
      setJourneyType(checklist.data.context.id);
      setIsDefault(checklist.data.is_default);
      setStages(
        checklist.data.checklist_stages.sort(orderIndexSort).map((stage) => ({
          id: stage.id,
          name: stage.name,
          order_index: stage.order_index,
          tasks: stage.checklist_tasks.sort(orderIndexSort).map((task) => ({
            id: task.id,
            name: task.name,
            description: task.description,
            order_index: task.order_index,
          })),
        })),
      );
    }
  }, [checklist]);

  const stageIcons = (stageIndex: number): ReactNode => (
    <Button
      type="button"
      label="Remove"
      outlined={true}
      className="p-button-danger p-button-sm w-auto"
      disabled={stages.length <= 1}
      onClick={() => onRemoveStageClick(stageIndex)}
    />
  );

  const taskIcons = (stageIndex: number, taskIndex: number): ReactNode => (
    <Button
      type="button"
      label="Remove"
      outlined={true}
      className="p-button-danger p-button-sm w-auto"
      disabled={stages[stageIndex].tasks.length <= 1}
      onClick={() => onRemoveStageTaskClick(stageIndex, taskIndex)}
    />
  );

  return (
    <form onSubmit={handleSubmit} className="col-12">
      <div className="p-fluid">
        <Panel header="General" toggleable={true} className="mb-3">
          <div className="field pt-3">
            <div className="p-float-label">
              <InputText value={name} onChange={onNameChange} />
              <label>Name</label>
            </div>
          </div>

          <div className="field pt-3">
            <div className="p-float-label">
              <Dropdown
                options={journeyTypesRes?.data.results || []}
                value={journeyType}
                onChange={onJourneyTypeChange}
                optionValue="id"
                optionLabel="name"
              />
              <label>Context</label>
            </div>
          </div>

          <div className="field-checkbox pt-3">
            <Checkbox
              checked={isDefault}
              onChange={onIsDefaultChange}
              inputId="isDefault"
            />
            <label htmlFor="isDefault">Default</label>
          </div>

          {/* stage */}
          {stages.map((stage, i) => (
            <div key={i} className="mb-3">
              <div className="py-3 flex align-items-center mb-3">
                <strong className="mr-3">Stage {i + 1}</strong>
                {stageIcons(i)}
              </div>

              <div className="grid formgrid">
                <div className="field col pt-3">
                  <div className="p-float-label">
                    <InputText
                      value={stage.name}
                      onChange={(e) => onStageNameChange(e, i)}
                      placeholder="Name"
                    />
                    <label>Name</label>
                  </div>
                </div>

                <div className="field col pt-3">
                  <div className="p-float-label">
                    <InputNumber
                      value={stage.order_index}
                      onChange={(e) => onStageOrderIndexChange(e, i)}
                      placeholder="Order index"
                      min={1}
                    />
                    <label>Order index:</label>
                  </div>
                </div>
              </div>

              {/* task */}
              {stage.tasks.map((task, j) => (
                <div key={j}>
                  <div className="grid formgrid">
                    <div className="field col pt-3">
                      <div className="p-float-label">
                        <InputText
                          value={task.name}
                          onChange={(e) => onStageTaskNameChange(e, i, j)}
                          placeholder="Name"
                        />
                        <label>Name</label>
                      </div>
                    </div>

                    <div className="field col pt-3">
                      <div className="p-float-label">
                        <InputText
                          value={task.description}
                          onChange={(e) =>
                            onStageTaskDescriptionChange(e, i, j)
                          }
                          placeholder="Description"
                        />
                        <label>Description</label>
                      </div>
                    </div>

                    <div className="field col pt-3">
                      <div className="p-float-label">
                        <InputNumber
                          value={task.order_index}
                          onChange={(e) => onStageTaskOrderIndexChange(e, i, j)}
                          placeholder="Order index"
                          min={1}
                        />
                        <label>Order index</label>
                      </div>
                    </div>
                    <div className="field col pt-3">{taskIcons(i, j)}</div>
                  </div>
                </div>
              ))}

              <Button
                type="button"
                label="Add task"
                outlined={true}
                className="p-button-secondary p-button-sm w-auto"
                onClick={() => onNewTaskClick(i)}
              />
            </div>
          ))}

          <div className="flex gap-1 mb-3">
            <Button
              type="button"
              label="Add stage"
              outlined={true}
              className="p-button-secondary p-button-sm w-auto"
              onClick={onNewStageClick}
            />
          </div>
        </Panel>
      </div>

      <div className="flex gap-1 justify-content-end">
        {uuid && (
          <Button
            type="button"
            label="Remove"
            outlined={true}
            className="p-button-sm p-button-danger"
            onClick={onDelete}
          />
        )}

        <Button
          type="submit"
          label="Save"
          className="p-button-main p-button-sm"
          value="save"
        />

        <Button
          type="submit"
          label="Save new"
          className="p-button-main p-button-sm"
          value="save-new"
        />
      </div>
    </form>
  );
}

interface FormStageValue {
  id: string | null;
  name: string;
  order_index: number;
  tasks: Array<{
    id: string | null;
    name: string;
    description: string;
    order_index: number;
  }>;
}
